import React, { useCallback, useEffect, useState } from "react";
import { DatePicker, Form, Input, Select } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import { dateFormat, filterOptions, STATUS_LIST, TYPES } from "./variables";
import useStoreFilters from "customHooks/useStoreFilters";
import { FILTER_PAGE } from "utils/constants";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import moment from "moment/moment";
import { inputPatternTitle, inputTextPattern } from "components/Common/inputs/variables";

const { Option } = Select;
const { RangePicker } = DatePicker;

const useStyles = makeStyles({
  root: { width: "250px!important" },

  filtersContainer: {
    display: "flex",
    alignItems: "center",
    height: 60,
    overflow: "auto",

    "@media (max-width: 576px)": {
      height: "auto",
      flexDirection: "column",
      width: "100%",
      overflow: "hidden",
      gap: 10,
    },

    "& .ant-select-selection-overflow": {
      flexWrap: "nowrap",
    },
  },
  value: {
    width: "auto !important",
  },
  range: {
    width: "100px!important",
  },
});

const BundlesFilter = ({ onFilter }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const { filters } = useStoreFilters(FILTER_PAGE.GAS_BUNDLES);
  const [activeFilter, setFilter] = useState(null);

  const handleFilterChange = useCallback(
    value => {
      setFilter(value);
      form.setFieldsValue({
        type: value,
        value: null,
      });
    },
    [form]
  );

  useEffect(() => {
    const { pageSize, ...dataToSet } = filters;
    if (filters?.activationDateFrom) {
      setFilter("date");
      form.setFieldsValue({
        type: "date",
        value: [moment(filters?.activationDateFrom), moment(filters?.activationDateTo)],
      });
      return;
    }
    if (!!Object.keys(dataToSet)[0]) {
      setFilter(Object.keys(dataToSet)[0]);
      form.setFieldsValue({
        value: dataToSet[Object.keys(dataToSet)[0]],
        type: Object.keys(dataToSet)[0],
      });
    }
  }, [filters]);

  const onFinish = useCallback(
    values => {
      const normalizedFilters = !!filters?.pageSize ? { pageSize: filters.pageSize } : {};
      if (!(values.value && !values.type)) {
        if (values?.type === "date") {
          const filter = {};
          filter.activationDateFrom = customDateFormatWithoutTime(moment(values?.value[0]));
          filter.activationDateTo = customDateFormatWithoutTime(moment(values?.value[1]));
          onFilter(filter);
          return;
        }
        onFilter({ ...normalizedFilters, [values.type]: values.value?.trim() });
      }
    },
    [activeFilter, filters]
  );

  return (
    <>
      <div className="custom-filter">
        <Form form={form} layout="inline" onFinish={onFinish}>
          <div className={classes.filtersContainer}>
            <Form.Item name="type">
              <Select
                style={{ width: "100%" }}
                tokenSeparators={[","]}
                size="default"
                placeholder="Select type"
                allowClear
                onChange={handleFilterChange}
              >
                {filterOptions.length > 0 &&
                  filterOptions.map(item => (
                    <Option key={item.value} value={item.value}>
                      {item.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            {!!activeFilter && <div className="filter-divider" />}
            {activeFilter === "type" && (
              <Form.Item name="value" className={classes.value}>
                <Select showSearch size="default" placeholder="Select type" allowClear>
                  {TYPES.map(item => (
                    <Option key={`${item.value}-option`} value={item.value}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {activeFilter === "status" && (
              <Form.Item name="value" className={classes.value}>
                <Select showSearch size="default" placeholder="Select status" allowClear>
                  {STATUS_LIST.map(item => (
                    <Option key={`${item}-option`} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {activeFilter === "date" && (
              <Form.Item
                name="value"
                rules={[{ required: true, message: "Please select date range" }]}
                className={classes.root}
              >
                <RangePicker inputReadOnly={true} format={dateFormat} size={"default"} />
              </Form.Item>
            )}

            {(activeFilter === "id" || activeFilter === "name") && (
              <Form.Item name="value">
                <Input placeholder="Enter value" pattern={inputTextPattern} title={inputPatternTitle} />
              </Form.Item>
            )}
          </div>

          <CustomButton type="primary" htmlType="submit" text="Filter" size="small" />
        </Form>
      </div>
    </>
  );
};

export default BundlesFilter;
